import { useMemo } from "react";
import { ZustandStore } from "../../../store/zustand-store";
import "../../css/PricingTable.scss";
import { useNavigate } from "react-router-dom";
import SemButton from "../../../common/components/Button";
import backIcon from "../../Assets/common/back.svg";

const PricingTable = () => {
  const navigate = useNavigate();
  const email = localStorage.getItem("admin_mail");
  const customer_plan = ZustandStore((state) => state.customer_plan);

  const llmPlans = useMemo(() => {
    return [
      {
        id: "starter",
        name: "Starter",
        description: "Perfect for individuals and small projects",
        price: 29,
        originalPrice: 39,
        popular: false,
        features: [
          "25 prompts",
          "Up to 2 projects",
          "Daily updates",
          "5 users",
          "Reporting",
          "White label reports",
        ],
        comparison: {
          prompts: "25",
          projects: "2",
          users: "5",
          dailyUpdates: true,
          reporting: true,
          whiteLabel: false,
        },
        isActive: customer_plan?.llm_project === 2,
        link: `https://buy.stripe.com/7sYdR90g21fS3LDgA90Fi1f?prefilled_email=${email}`,
      },
      {
        id: "basic",
        name: "Growth",
        description: "Great for growing businesses and teams",
        price: 59,
        originalPrice: 79,
        popular: true,
        features: [
          "50 prompts",
          "Up to 5 projects",
          "Daily updates",
          "10 users",
          "Reporting",
          "White label reports",
        ],
        comparison: {
          prompts: "50",
          projects: "5",
          users: "10",
          dailyUpdates: true,
          reporting: true,
          whiteLabel: true,
        },
        isActive: customer_plan?.llm_project === 5,
        link: `https://buy.stripe.com/7sY00jd2OcYAaa10Bb0Fi1g?prefilled_email=${email}`,
      },
      {
        id: "advanced",
        name: "Scaler",
        description: "For enterprises and power users",
        price: 159,
        originalPrice: 199,
        popular: false,
        features: [
          "150 prompts",
          "Unlimited projects",
          "Daily updates",
          "50 users",
          "Reporting",
          "White label reports",
        ],
        comparison: {
          prompts: "150",
          projects: "Unlimited",
          users: "50",
          dailyUpdates: true,
          reporting: true,
          whiteLabel: true,
        },
        isActive: customer_plan?.llm_project > 5,
        link: `https://buy.stripe.com/4gMcN59QC2jW4PH83D0Fi1h?prefilled_email=${email}`,
      },
    ];
  }, [customer_plan]);

  const comparisonFeatures = [
    { key: "projects", label: "Projects" },
    { key: "prompts", label: "Prompts" },
    { key: "users", label: "Users" },
    { key: "dailyUpdates", label: "Daily Updates" },
    { key: "reporting", label: "Reporting" },
    { key: "whiteLabel", label: "White label reports" },
  ];

  const handlePlanSelect = (link) => {
    window.open(link, "_blank");
  };

  const CheckIcon = ({ icon }) => (
    <div
      style={{
        background: icon
          ? "linear-gradient(135deg, #4CAF50, #45a049) "
          : "linear-gradient(135deg, #f50d34, #ed0505)",
      }}
      className="check-icon"
    >
      {icon ? <span>✓</span> : <span> ✕</span>}
    </div>
  );

  return (
    <div className="pricing-container">
      {customer_plan?.llm_project ? (
        <SemButton
          name="Back"
          title="Back"
          onClick={() => navigate(-1)}
          icon={<img src={backIcon} alt="back button" />}
        />
      ) : null}
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Select the perfect plan for your needs and start growing today</p>
      </div>

      <div className="pricing-grid">
        {llmPlans?.map((plan) => (
          <div
            key={plan.id}
            className={`pricing-card ${plan.id} ${plan.popular ? "popular" : ""
              }`}
          >
            {plan.popular && <div className="popular-badge">Most Popular</div>}

            <div className="plan-header">
              <h3 className="plan-name">{plan.name}</h3>
            </div>

            <div className="price">
              <div className="price-container">
                <span className="original-price">${plan.originalPrice}</span>
                <span className="discounted-price">${plan.price}</span>
              </div>
              <span className="price-period">/month</span>
            </div>

            <ul className="features">
              {plan.features.map((feature, index) => {
                const keyMap = {
                  "Daily updates": "dailyUpdates",
                  Reporting: "reporting",
                  "White label reports": "whiteLabel",
                };

                const comparisonKey = keyMap[feature];
                const isBooleanFeature =
                  comparisonKey &&
                  typeof plan.comparison[comparisonKey] === "boolean";
                const isAvailable = isBooleanFeature
                  ? plan.comparison[comparisonKey]
                  : true;

                return (
                  <li key={index} className="flex items-center gap-2">
                    {isAvailable ? (
                      <CheckIcon
                        icon={true}
                        className="h-5 w-5 text-green-600"
                      />
                    ) : (
                      <div>
                        <CheckIcon
                          icon={false}
                          className="h-5 w-5 text-green-600"
                        />
                      </div>
                    )}
                    <span>{feature}</span>
                  </li>
                );
              })}
            </ul>

            <button
              className="cta-button"
              disabled={plan?.isActive}
              onClick={() => handlePlanSelect(plan?.link)}
            >
              {plan?.isActive ? "Current Plan" : "Buy Now"}
            </button>
          </div>
        ))}
      </div>

      <div className="features-comparison">
        <h2 className="comparison-title">Feature Comparison</h2>
        <div className="table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                <th>Features</th>
                {llmPlans?.map((plan) => (
                  <th key={plan.id}>{plan.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonFeatures.map((feature) => (
                <tr key={feature.key}>
                  <td className="feature-name">{feature.label}</td>
                  {llmPlans?.map((plan) => (
                    <td key={plan.id}>
                      {typeof plan.comparison[feature.key] === "boolean"
                        ? plan.comparison[feature.key]
                          ? "✅"
                          : "❌"
                        : plan.comparison[feature.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PricingTable;
